import {
  createContext,
  ReactNode,
  useRef,
  useMemo,
  useContext,
  useCallback,
} from 'react';
import debounce from 'lodash/debounce';
import sortBy from 'lodash/sortBy';
import { JSONContent } from '@tiptap/core';

import customNanoId from 'lib/nanoid/nanoid';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { useLoggingMutation } from 'lib/apollo/apollo-hooks';
import { modifyActivitiesPublishChanges } from 'approot/activity-builder/activity-builder.apollo.utils';
import type { TeachingLearningSequenceFragment } from './teaching-notes.graphql.generated';
import type {
  UpdateInteractiveTeachingLearningSequenceItemMutation,
  UpdateInteractiveTeachingLearningSequenceItemMutationVariables,
} from './teaching-notes.graphql.generated';
import {
  HighImpactTeachingStrategies,
  LEARNING_SEQUENCE_CATEGORY_MATERIALS_NEEDED,
  LEARNING_SEQUENCE_LESSON_MATERIAL_GROUP_TYPE_STUDENT,
  LearningSequenceItemCategories,
  LearningSequenceItemTypes,
  TeachingAndLearningSequenceTheETypes,
  VisibleThinkingActivities,
} from 'global/constants';
import { getLessonMaterialPayload } from './teaching-notes.utils';
import {
  DELETE_TEACHING_LEARNING_SEQUENCE_ITEM,
  INSERT_TEACHING_LEARNING_SEQUENCE_ITEM,
  UPDATE_TEACHING_LEARNING_SEQUENCE_ITEM,
} from './teaching-notes.graphql';
import {
  getNoteFromCache,
  updateNoteInCache,
  updateSequencesInCache,
} from './teaching-notes.apollo';
import type {
  DeleteTeachingLearningSequenceItemMutation,
  DeleteTeachingLearningSequenceItemMutationVariables,
} from './teaching-notes.graphql.generated';
import type {
  InsertTeachingLearningSequenceItemMutation,
  InsertTeachingLearningSequenceItemMutationVariables,
} from './teaching-notes.graphql.generated';
import { ComponentActivity } from '../component.constants';
import type { LessonMaterialInput } from '../../../../gql/schema-types';
import type { LessonMaterialFragment } from '../../../shared/lesson-materials/lesson-material-fragment.generated';
import { useActivityBuilderRequest } from 'approot/activity-builder/activity-builder-request.apollo';

type NoteInputs = Map<string, TeachingLearningSequenceFragment>;
type SequenceInputs = Map<string, TeachingLearningSequenceFragment[] | null>;

type BatchComponentUpdate = {
  addNote: (
    category: LearningSequenceItemCategories,
    type: LearningSequenceItemTypes,
    sequenceItems: TeachingLearningSequenceFragment[] | null,
    component: ComponentActivity | null,
    data: JSONContent,
    sort?: string | null
  ) => void;
  updateNote: (
    input: TeachingLearningSequenceFragment,
    sequenceItems: TeachingLearningSequenceFragment[] | null
  ) => void;
  deleteNote: (
    input: TeachingLearningSequenceFragment,
    sequenceItems: TeachingLearningSequenceFragment[] | null
  ) => void;
  deleteSyncToServer: (id: string) => void;
};

export const BatchTeachingNotesContext = createContext<BatchComponentUpdate>({
  addNote: () => {},
  updateNote: () => {},
  deleteNote: () => {},
  deleteSyncToServer: () => {},
});

export const BatchTeachingNotesContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { createRequest, clearRequest } = useActivityBuilderRequest();
  const { showError } = useSnackbar();
  const noteInputsRef = useRef<NoteInputs>(new Map());
  const noteClonesRef = useRef<NoteInputs>(new Map());
  const sequenceCloneRef = useRef<SequenceInputs>(new Map());
  const noteSyncMapRef = useRef<Map<string, Function>>(new Map());

  const { contentId, contentType, userClassId } =
    useGetInteractiveLessonParams();

  const [updateSequenceNote, { client }] = useLoggingMutation<
    UpdateInteractiveTeachingLearningSequenceItemMutation,
    UpdateInteractiveTeachingLearningSequenceItemMutationVariables
  >(UPDATE_TEACHING_LEARNING_SEQUENCE_ITEM);

  const [deleteSequenceNote] = useLoggingMutation<
    DeleteTeachingLearningSequenceItemMutation,
    DeleteTeachingLearningSequenceItemMutationVariables
  >(DELETE_TEACHING_LEARNING_SEQUENCE_ITEM);

  const [addSequenceNote] = useLoggingMutation<
    InsertTeachingLearningSequenceItemMutation,
    InsertTeachingLearningSequenceItemMutationVariables
  >(INSERT_TEACHING_LEARNING_SEQUENCE_ITEM);

  const syncToServer = useCallback(
    ({
      input: noteInput,
      inputClones,
      requestId,
      onComplete,
    }: {
      input: TeachingLearningSequenceFragment;
      inputClones: NoteInputs;
      requestId: string;
      onComplete: (requestId: string) => void;
    }) => {
      const {
        component,
        __typename,
        lessonMaterials,
        connections,
        theEsDetails,
        visibleThinkingActivitiesDetails,
        highImpactTeachingStrategiesDetails,
        activityOutcomes,
        ...rest
      } = noteInput;

      updateSequenceNote({
        fetchPolicy: 'no-cache',
        ignoreResults: true,
        variables: {
          input: {
            ...rest,
            contentId,
            contentType,
            componentId: noteInput.component?.id,
            lessonMaterials:
              noteInput.category === LEARNING_SEQUENCE_CATEGORY_MATERIALS_NEEDED
                ? getLessonMaterialPayload(lessonMaterials)
                : null,
            connections: connections
              ? connections.flatMap(c => c.outcomes).map(o => o.code)
              : null,
            theEs: theEsDetails?.map(
              te => te.id
            ) as TeachingAndLearningSequenceTheETypes[],
            visibleThinkingActivities: visibleThinkingActivitiesDetails?.map(
              vta => vta.id
            ) as VisibleThinkingActivities[],
            highImpactTeachingStrategies:
              highImpactTeachingStrategiesDetails?.map(
                item => item.id
              ) as HighImpactTeachingStrategies[],
          },
        },
      })
        .then(() => {
          modifyActivitiesPublishChanges({
            client,
            id: contentId,
            contentType,
          });
        })
        .catch(() => {
          const noteClone = inputClones.get(noteInput.id);
          if (noteClone) {
            updateNoteInCache({
              client,
              note: noteClone,
            });
          }
          const sequenceClone = sequenceCloneRef.current.get(noteInput.id);
          updateSequencesInCache({
            client,
            contentId,
            contentType,
            classId: userClassId,
            sequences: sequenceClone || [],
          });

          showError(
            'There was an error updating the teaching note. Please try again'
          );
        })
        .finally(() => {
          sequenceCloneRef.current.delete(noteInput.id);
          inputClones.delete(noteInput.id);
          onComplete(requestId);
        });
    },
    [client, contentId, contentType, userClassId, updateSequenceNote, showError]
  );

  const updateNote = useCallback(
    (
      input: TeachingLearningSequenceFragment,
      sequenceItems: TeachingLearningSequenceFragment[] | null
    ) => {
      noteInputsRef.current.set(input.id, input);
      sequenceCloneRef.current.set(input.id, sequenceItems);

      // create a backup in case of an error
      const noteClone = noteClonesRef.current.get(input.id);
      if (!noteClone) {
        const noteFromCache = getNoteFromCache(client, input.id);
        if (noteFromCache) {
          noteClonesRef.current.set(input.id, noteFromCache);
        }
      }

      updateNoteInCache({ client, note: input });
      updateSequencesInCache({
        client,
        contentId,
        contentType,
        classId: userClassId,
        sequences: sortBy(
          [...(sequenceItems?.filter(s => s.id !== input.id) || []), input],
          'sort'
        ),
      });

      if (!noteSyncMapRef.current.get(input.id)) {
        // create a new debounce function for this componentId if it doesn't exist
        noteSyncMapRef.current.set(
          input.id,
          debounce((input, cloneRefs, onComplete) => {
            const requestId = createRequest(input.id);
            syncToServer({
              input,
              inputClones: cloneRefs,
              onComplete,
              requestId,
            });
          }, 300)
        );
      }

      // call the debounced function for this componentId
      const sync = noteSyncMapRef.current.get(input.id);
      if (sync) {
        sync(input, noteClonesRef.current, (requestId: string) => {
          clearRequest(requestId);
        });
      }
    },
    [
      client,
      contentId,
      contentType,
      userClassId,
      syncToServer,
      createRequest,
      clearRequest,
    ]
  );

  const deleteNote = useCallback(
    (
      input: TeachingLearningSequenceFragment,
      sequenceItems: TeachingLearningSequenceFragment[] | null
    ) => {
      // create a backup in case of an error
      noteInputsRef.current.set(input.id, input);
      sequenceCloneRef.current.set(input.id, sequenceItems || []);

      updateSequencesInCache({
        client,
        contentId,
        contentType,
        classId: userClassId,
        sequences: sequenceItems?.filter(s => s.id !== input.id) || [],
      });
    },
    [client, contentId, contentType, userClassId]
  );

  const deleteSyncToServer = useCallback(
    (id: string) => {
      const noteInput = noteInputsRef.current.get(id);
      if (!noteInput) return;

      const requestId = createRequest(noteInput.id);

      deleteSequenceNote({
        fetchPolicy: 'no-cache',
        ignoreResults: true,
        variables: {
          input: {
            id: noteInput.id,
            contentId,
            contentType,
          },
        },
      })
        .then(() => {
          modifyActivitiesPublishChanges({
            client,
            id: contentId,
            contentType,
          });
        })
        .catch(() => {
          const sequenceClone = sequenceCloneRef.current.get(id);
          updateSequencesInCache({
            client,
            contentId,
            contentType,
            classId: userClassId,
            sequences: sequenceClone || [],
          });
          showError(
            'There was an error deleting the teaching note. Please try again'
          );
        })
        .finally(() => {
          sequenceCloneRef.current.delete(id);
          noteInputsRef.current.delete(id);
          clearRequest(requestId);
        });
    },
    [
      client,
      contentId,
      contentType,
      userClassId,
      showError,
      deleteSequenceNote,
      createRequest,
      clearRequest,
    ]
  );

  const addNote = useCallback(
    (
      category: LearningSequenceItemCategories,
      type: LearningSequenceItemTypes,
      sequenceItems: TeachingLearningSequenceFragment[] | null,
      component: ComponentActivity | null,
      data: JSONContent,
      sort: string | null = null
    ) => {
      const newLessonMaterial: LessonMaterialInput | undefined =
        category === LEARNING_SEQUENCE_CATEGORY_MATERIALS_NEEDED
          ? {
              id: customNanoId(),
              name: '',
              numberOfItems: 1,
              groupType: LEARNING_SEQUENCE_LESSON_MATERIAL_GROUP_TYPE_STUDENT,
              numberInGroup: null,
            }
          : undefined;

      const newNote = {
        id: customNanoId(),
        category,
        type,
        data,
        contentId,
        contentType,
        componentId: component ? component.id : null,
        sort,
        lessonMaterials: newLessonMaterial ? [newLessonMaterial] : null,
      };

      const requestId = createRequest(newNote.id);

      updateSequencesInCache({
        client,
        contentId,
        contentType,
        classId: userClassId,
        sequences: [
          ...(sequenceItems || []),
          {
            ...newNote,
            component,
            lessonMaterials: newLessonMaterial
              ? [
                  {
                    __typename: 'LessonMaterial',
                    ...newLessonMaterial,
                    normalisedNumberOfItems: null,
                    materialId: null,
                    alternativeMaterials: null,
                    isOptional: false,
                    note: null,
                    isReusable: false,
                    isStandardised: true,
                    unitOfMeasurementId: null,
                    unitsOfMeasurement: [],
                  } as LessonMaterialFragment,
                ]
              : null,
            theEsDetails: null,
            visibleThinkingActivitiesDetails: null,
            highImpactTeachingStrategiesDetails: null,
            connections: null,
            sectionId: null,
            activityOutcomes: null,
            __typename: 'ActivitiesTeachingLearningSequenceItem',
          },
        ],
      });
      addSequenceNote({
        variables: {
          input: {
            ...newNote,
            connections: null,
          },
        },
      })
        .then(() => {
          modifyActivitiesPublishChanges({
            client,
            id: contentId,
            contentType,
          });
        })
        .catch(() => {
          updateSequencesInCache({
            client,
            contentId,
            contentType,
            classId: userClassId,
            sequences: [...(sequenceItems || [])],
          });
          showError(
            'There was a problem adding the teaching note. Please try again'
          );
        })
        .finally(() => {
          clearRequest(requestId);
        });
    },
    [
      client,
      contentId,
      contentType,
      userClassId,
      showError,
      addSequenceNote,
      createRequest,
      clearRequest,
    ]
  );

  const debounceDeleteSyncToServer = useMemo(
    () => debounce(deleteSyncToServer, 500),
    [deleteSyncToServer]
  );

  const value = useMemo(
    () => ({
      updateNote,
      addNote,
      deleteNote,
      deleteSyncToServer: debounceDeleteSyncToServer,
    }),
    [updateNote, deleteNote, addNote, debounceDeleteSyncToServer]
  );

  return (
    <BatchTeachingNotesContext.Provider value={value}>
      {children}
    </BatchTeachingNotesContext.Provider>
  );
};

export const useBatchTeachingNotesContext = () => {
  const { addNote, updateNote, deleteNote, deleteSyncToServer } = useContext(
    BatchTeachingNotesContext
  );

  const handleUpdate = useCallback(
    (
      note: TeachingLearningSequenceFragment,
      sequences: TeachingLearningSequenceFragment[]
    ) => {
      updateNote(note, sequences);
    },
    [updateNote]
  );

  const handleDelete = useCallback(
    (
      note: TeachingLearningSequenceFragment,
      sequences: TeachingLearningSequenceFragment[]
    ) => {
      deleteNote(note, sequences);
      deleteSyncToServer(note.id);
    },
    [deleteNote, deleteSyncToServer]
  );

  return {
    handleUpdate,
    handleDelete,
    handleAdd: addNote,
  };
};
